import React, { useState } from "react";
import { motion } from "framer-motion";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { CheckCircle2, Send, Loader2 } from "lucide-react";

const EMPTY = { name: "", business_name: "", business_type: "", phone: "", email: "", message: "", website: "" };
const TYPES = [["manufacturer", "Manufacturer"], ["distributor", "Distributor"], ["wholesaler", "Wholesaler"], ["retailer", "Retailer"]];

function validate(f) {
  if (f.name.trim().length < 2) return "Please enter your name";
  if (f.business_name.trim().length < 2) return "Please enter your business name";
  if (!f.business_type) return "Please choose your business type";
  if (!/^\+?[0-9 ]{8,16}$/.test(f.phone.trim())) return "Please enter a valid phone number";
  if (!/^\S+@\S+\.\S+$/.test(f.email.trim())) return "Please enter a valid email";
  if (f.message.trim().length < 5) return "Please tell us a little about what you need";
  return null;
}

const field = "h-12 rounded-none border-0 border-b border-[#D6CFC2] bg-transparent px-0 text-base shadow-none focus-visible:border-[#0D5C3A] focus-visible:ring-0";

export function ContactSection() {
  const [f, setF] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    toast.dismiss();
    const err = validate(f);
    if (err) { toast.error(err); return; }
    setBusy(true);
    try { await api.post("/enquiries", { ...f, phone: f.phone.trim(), email: f.email.trim() }); setSent(true); setF(EMPTY); }
    catch (ex) { toast.error(ex?.response?.data?.detail?.[0]?.msg || ex?.response?.data?.detail || "Could not send. Please try again."); }
    finally { setBusy(false); }
  };

  return (
    <section id="contact" className="mx-auto max-w-7xl scroll-mt-28 px-6 pb-24 sm:pb-32" data-testid="contact-section">
      <div className="grid gap-14 border-t border-[#E5E0D8] pt-20 lg:grid-cols-12">
        <motion.div initial={{ opacity: 0, y: 18 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.55 }} className="lg:col-span-5">
          <p className="text-xs font-bold uppercase tracking-[0.22em] text-[#C85A32]">Talk to us</p>
          <h2 className="mt-4 font-heading text-3xl font-extrabold tracking-tight text-stone-900 sm:text-4xl">Not sure yet? Ask us anything before you register.</h2>
          <p className="mt-6 max-w-md text-base leading-relaxed text-stone-600">Tell us about your business and what you're looking for. Our team will reach out by phone or email.</p>
        </motion.div>
        <div className="lg:col-span-7">
          {sent ? (
            <div className="flex flex-col items-start gap-4 py-10" data-testid="contact-success">
              <CheckCircle2 className="h-10 w-10 text-[#0D5C3A]" />
              <p className="font-heading text-2xl font-bold text-stone-900">Thanks — we've got your enquiry.</p>
              <p className="text-stone-600">We'll get back to you shortly.</p>
              <Button variant="ghost" className="rounded-full px-0 text-[#0D5C3A] hover:bg-transparent hover:underline" onClick={() => setSent(false)} data-testid="contact-send-another">Send another enquiry</Button>
            </div>
          ) : (
            <form onSubmit={submit} className="grid gap-x-8 gap-y-6 sm:grid-cols-2" data-testid="contact-form" noValidate>
              <Input className={field} placeholder="Your name" value={f.name} onChange={set("name")} maxLength={80} data-testid="contact-name-input" />
              <Input className={field} placeholder="Business name" value={f.business_name} onChange={set("business_name")} maxLength={120} data-testid="contact-business-input" />
              <Select value={f.business_type} onValueChange={(v) => setF({ ...f, business_type: v })}>
                <SelectTrigger className={`${field} ${f.business_type ? "" : "text-stone-500"}`} data-testid="contact-type-select"><SelectValue placeholder="Business type" /></SelectTrigger>
                <SelectContent>{TYPES.map(([v, l]) => <SelectItem key={v} value={v} data-testid={`contact-type-${v}`}>{l}</SelectItem>)}</SelectContent>
              </Select>
              <Input className={field} type="tel" placeholder="Phone (e.g. +91 98765 43210)" value={f.phone} onChange={set("phone")} maxLength={16} data-testid="contact-phone-input" />
              <Input className={`${field} sm:col-span-2`} type="email" placeholder="Email" value={f.email} onChange={set("email")} data-testid="contact-email-input" />
              <Textarea className="min-h-[120px] resize-none rounded-none border-0 border-b border-[#D6CFC2] bg-transparent px-0 text-base shadow-none focus-visible:border-[#0D5C3A] focus-visible:ring-0 sm:col-span-2" placeholder="What would you like to know?" value={f.message} onChange={set("message")} maxLength={1500} data-testid="contact-message-input" />
              <input type="text" tabIndex={-1} autoComplete="off" value={f.website} onChange={set("website")} className="hidden" aria-hidden="true" />
              <div className="sm:col-span-2">
                <Button type="submit" size="lg" disabled={busy} className="h-12 rounded-full bg-[#0D5C3A] px-8 text-base transition-transform hover:scale-[1.02] hover:bg-[#084229]" data-testid="contact-submit-btn">
                  {busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Send className="mr-2 h-4 w-4" />} Send enquiry
                </Button>
              </div>
            </form>
          )}
        </div>
      </div>
    </section>
  );
}
